"use client";

import { useCallback, useState } from "react";

type Props = {
  title: string;
  text: string;
  path: string; // e.g. "/festivals/reading"
};

export function ShareButton({ title, text, path }: Props) {
  const [toast, setToast] = useState<string | null>(null);

  const handleShare = useCallback(async () => {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "https://peakwave.co";
    const url = `${origin}${path}`;
    const fullText = `${text} ${url}`;

    const canNativeShare =
      typeof navigator !== "undefined" && typeof navigator.share === "function";

    if (canNativeShare) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        if ((err as DOMException)?.name === "AbortError") return;
        // fall through to clipboard on any other error
      }
    }

    try {
      await navigator.clipboard.writeText(fullText);
      setToast("Link copied to clipboard");
      setTimeout(() => setToast(null), 2500);
    } catch {
      setToast("Copy failed, long-press the URL to share");
      setTimeout(() => setToast(null), 3000);
    }
  }, [title, text, path]);

  return (
    <>
      <button
        type="button"
        onClick={handleShare}
        aria-label={`Share ${title}`}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/15 hover:bg-white/25 border border-white/25 text-white text-[13.5px] font-semibold transition-colors min-h-[44px] backdrop-blur-sm"
      >
        <ShareIcon />
        Share
      </button>
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-navy text-white text-[13.5px] font-semibold px-5 py-3 rounded-full shadow-[0_6px_20px_rgba(0,0,0,0.2)] z-50"
        >
          {toast}
        </div>
      )}
    </>
  );
}

function ShareIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M16 6l-4-4-4 4M12 2v14"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
